import { type useCallback, useRef, useMemo, useEffect } from 'react';

/**
 * Track the expiration of a media token and refetch it when it expires.
 * @param mediaToken The JSON web token provided by the media service.
 * @param refetch Function to run when token expires.
 */
export const useRefetchMediaTokenOnExpired = ({
	mediaToken,
	refetch,
	shouldWaitUntilBrowserActive,
}: {
	mediaToken: string | null;
	/**
	 * Refetch function to run when token expires.
	 * This function should be using a useCallback to avoid unnecessary re-renders.
	 */
	refetch: ReturnType<typeof useCallback<() => void>>;
	shouldWaitUntilBrowserActive?: boolean;
}) => {
	const timerRef = useRef<number | null>(null);

	const expiration = useMemo(() => {
		try {
			if (!mediaToken) {
				return null;
			}

			return JSON.parse(atob(mediaToken.split('.')[1])).exp;
		} catch (err) {
			// noop
			return null;
		}
	}, [mediaToken]);

	useEffect(() => {
		if (!expiration) {
			if (timerRef.current) {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.clearTimeout(timerRef.current);
			}
			return;
		}

		const expirationDate = new Date(expiration * 1000);
		// extra 2 seconds to account for network latency
		const expirationTimeout = expirationDate.getTime() - Date.now() - 2000;

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		timerRef.current = window.setTimeout(() => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			if (shouldWaitUntilBrowserActive && document.visibilityState !== 'visible') {
				const handleVisibilityChange = () => {
					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					if (document.visibilityState === 'visible') {
						// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
						document.removeEventListener('visibilitychange', handleVisibilityChange);
						refetch();
					}
				};

				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				document.addEventListener('visibilitychange', handleVisibilityChange);

				return;
			}

			refetch();
		}, expirationTimeout);

		return () => {
			if (timerRef.current) {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.clearTimeout(timerRef.current);
			}
		};
	}, [expiration, refetch, shouldWaitUntilBrowserActive]);
};
